import {Component, OnDestroy, OnInit} from '@angular/core';
import {Subject, Subscription} from 'rxjs';
import {debounceTime} from 'rxjs/operators';
import {User} from '@models/user';
import {CommonService} from '@services/common.service';
import {EMPTY_USER} from '@models/empty';
import {UserService} from '@services/user.service';
import {AuthenticationService} from '@services/authentification.service';
import {TrackingService} from '@services/tracking.service';
import {MatDialog} from '@angular/material/dialog';
import {UpgradePlanV2Component} from '@components/popup/upgrade-plan-v2/upgrade-plan-v2.component';
import {DemoAccountComponent} from '@components/popup/demo-account/demo-account.component';

// Point d'enregistrement : on attend la fin de la frappe pour décider quoi envoyer (le vrai
// debounce réseau, lui, est dans UserService.updateSettings).
const SAVE_DEBOUNCE = 600;

@Component({
    selector: 'app-settings',
    templateUrl: './settings.component.html',
    styleUrls: ['./settings.component.scss']
})
export class SettingsComponent implements OnInit, OnDestroy {

    user: User = Object.assign({}, EMPTY_USER);

    private save$ = new Subject<void>();
    private saveSub: Subscription;
    private pending = false;
    // Dernière valeur valide de chaque champ numérique, pour ne jamais enregistrer un null
    private lastValid: { [key: string]: number } = {};

    constructor(
        public commonService: CommonService,
        private userService: UserService,
        public dialog: MatDialog,
        public authenticationService: AuthenticationService,
        public trackingService: TrackingService) {
    }

    ngOnInit(): void {
        this.user = this.authenticationService.currentUser;
        this.snapshot();
        this.saveSub = this.save$.pipe(debounceTime(SAVE_DEBOUNCE)).subscribe(() => this.save());
    }

    ngOnDestroy(): void {
        if (this.saveSub) {
            this.saveSub.unsubscribe();
        }
        // Quitter la page juste après une saisie ne doit pas perdre le dernier changement.
        // Sinon, on remet quand même les champs laissés vides : le modèle est l'objet partagé
        // currentUser, un null y resterait visible par le reste de l'app jusqu'au rechargement.
        if (this.pending) {
            this.save();
        } else {
            this.restoreInvalid();
        }
    }

    editFiscal(event) {
        if (this.user.fiscal_choice == "SCI IS" && !this.authenticationService.isAllowed('essentiel')) {
            const dialogRef = this.dialog.open(UpgradePlanV2Component, {
                width: '520px',
                data: {visuel: 'assets/upgrade/is.png', plan: 'Essentiel', feature: 'Fiscalité en ' + this.user.fiscal_choice}
            });
            this.user.fiscal_choice = 'SCI IR';
        } else if (this.user.fiscal_choice != 'Foncier forfait' && (this.user.pro_plan == '' && (this.user.plan == 'trial' || this.user.plan == ''))) {
            const dialogRef = this.dialog.open(UpgradePlanV2Component, {
                width: '520px',
                data: {visuel: 'assets/upgrade/is.png', plan: 'Essentiel', feature: 'Fiscalité en ' + this.user.fiscal_choice}
            });
            this.user.fiscal_choice = 'Foncier forfait';
        } else {
            this.edit(event)
        }
    }

    /**
     * Un champ a changé. Les saisies invalides d'un `type="number"` (champ vidé, « - » seul,
     * exposant en cours de frappe) arrivent ici en null : ngModel a déjà écrit null dans le
     * modèle. On n'enregistre pas tout de suite — le champ est restauré au moment de
     * l'enregistrement, donc la frappe invalide est sans effet sur la valeur en base.
     */
    edit(event) {
        if (this.isInvalid(event)) {
            return;
        }
        this.pending = true;
        this.save$.next();
    }

    private save() {
        this.pending = false;
        this.restoreInvalid();
        this.userService.updateSettings(this.user);
        this.snapshot();
    }

    private isInvalid(value): boolean {
        return value === null || value === undefined || (typeof value === 'number' && isNaN(value));
    }

    // Mémorise l'état valide courant des champs numériques
    private snapshot() {
        Object.keys(EMPTY_USER).forEach(key => {
            if (typeof EMPTY_USER[key] === 'number' && !this.isInvalid(this.user[key])) {
                this.lastValid[key] = this.user[key];
            }
        });
    }

    // Un champ laissé vide ne doit pas écraser sa valeur enregistrée : `cleanInput()` du service
    // ne rattrape pas null (isNaN(null) est faux), il partirait tel quel dans le PUT.
    private restoreInvalid() {
        Object.keys(this.lastValid).forEach(key => {
            if (this.isInvalid(this.user[key])) {
                this.user[key] = this.lastValid[key];
            }
        });
    }

    // Plan partenaire : jamais d'upsell, rappel des limites du compte démo à la place.
    openUpgrade() {
        if (this.authenticationService.isPartner) {
            this.dialog.open(DemoAccountComponent, {width: '420px'});
            return;
        }
        this.dialog.open(UpgradePlanV2Component, {
            width: '520px',
            data: {visuel: 'assets/upgrade/is.png', plan: 'Pro', feature: 'Chasseurs immobiliers'}
        });
    }
}
